import { ChevronDown, ChevronRight, RotateCw } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import { obtenerIniciales } from "@/lib/iniciales";
import { cn } from "@/lib/utils";
import type { AudioDeTerreno, CampoDeTerreno, FotoDeTerreno } from "@/lib/terreno";

import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { ImagenDeTerreno } from "./FotosDeTerreno";
import {
  atributoDeResaltado,
  CLASE_DE_FOTO_RESALTADA,
  CLASE_DE_TARJETA_RESALTADA,
  TagDeSincronizacion,
} from "./MarcasDeSincronizacion";
import { ReproductorDeAudioDeTerreno } from "./ReproductorDeAudioDeTerreno";
import { avisoDeTranscripcion, horaDeCaptura } from "./registroDeTerreno";
import {
  resumenDelCampo,
  textoDelResumen,
  type EstadoDelTerreno,
  type VistaDeTerreno,
} from "./terrenoDeAntecedentes";

interface PanelDeTerrenoDeLaDescripcionProps {
  informeId: number;
  terreno: Exclude<EstadoDelTerreno, { estado: "no_disponible" }>;
  /** El resumen ya resuelto del registro (`null` si todavía no llegó). */
  vista: VistaDeTerreno | null;
  /** Lo que mandó el campo para ESTA descripción. */
  campo: CampoDeTerreno | null;
  /** Quién tiene asignada la visita: da el nombre en "aún sin sincronizar". */
  asignado: string | null;
  /** Abre la pestaña "Registro de terreno". Ausente = no hay a dónde ir. */
  onVerTodo?: () => void;
  onAbrirFoto: (uuid: string) => void;
  /**
   * `lateral` (Suelos, Aguas, Construcciones) es el panel de al lado del campo;
   * `desplegable` es la caja plegable que el diseño dibuja BAJO el campo en
   * Maquinaria, Instalaciones y Plantaciones: arranca plegada y su cabecera
   * lleva las acciones y el interruptor «Ver registros» / «Ocultar».
   */
  variante?: "lateral" | "desplegable";
  /** Solo en `desplegable`: lo que va en la cabecera antes del interruptor («Redactar desde terreno»). */
  acciones?: ReactNode;
}

export function Rotulo({ children }: { children: ReactNode }) {
  return (
    <span className="text-ink-muted text-[0.59375rem] font-extrabold tracking-[0.07em] uppercase">
      {children}
    </span>
  );
}

/**
 * Lo que dice una captura resaltada: «SINC. 09:12» pegado a su rótulo. Vacío
 * cuando la captura no es de una sincronización resaltada.
 */
export function useResaltadoDeLaCaptura(sincronizacionId: string | null | undefined) {
  const resaltado = useResaltadoDeSincronizaciones();
  const on = resaltado.esResaltada(sincronizacionId);

  return {
    on,
    atributo: atributoDeResaltado(on),
    tag: on && sincronizacionId ? <TagDeSincronizacion horas={[resaltado.etiqueta(sincronizacionId)]} /> : null,
  };
}

export function TarjetaDeAudio({ informeId, audio }: { informeId: number; audio: AudioDeTerreno }) {
  const marca = useResaltadoDeLaCaptura(audio.sincronizacion_id);

  return (
    <div
      {...marca.atributo}
      className={cn(
        "rounded-[0.6875rem] border px-2.5 pt-2 pb-2.5",
        marca.on ? CLASE_DE_TARJETA_RESALTADA : "border-border bg-card",
      )}
    >
      <ReproductorDeAudioDeTerreno informeId={informeId} audio={audio} compacto />
      <div className="mt-1.5 flex items-center gap-1.5">
        <Rotulo>Transcripción</Rotulo>
        <span className="text-ink-hint font-mono text-[0.625rem] font-semibold">
          {horaDeCaptura(audio.capturado_en)}
        </span>
        {marca.tag}
        <span className="ml-auto">
          <BotonDescartarDeTerreno uuid={audio.uuid} etiqueta="nota de voz · registro de terreno" />
        </span>
      </div>
      {audio.transcripcion_estado === "lista" && audio.transcripcion ? (
        <p className="text-ink-body mt-0.5 text-xs leading-normal font-medium text-pretty">
          {audio.transcripcion}
        </p>
      ) : (
        <p className="text-ink-hint mt-0.5 text-xs leading-normal font-medium italic">
          {avisoDeTranscripcion(audio)}
        </p>
      )}
    </div>
  );
}

function TarjetaDeTexto({
  uuid,
  etiqueta,
  texto,
  capturadoEn,
  sincronizacionId,
}: {
  uuid: string;
  etiqueta: string;
  texto: string;
  capturadoEn: string | null;
  sincronizacionId: string | null | undefined;
}) {
  const marca = useResaltadoDeLaCaptura(sincronizacionId);

  return (
    <div
      {...marca.atributo}
      className={cn(
        "rounded-[0.6875rem] border px-2.5 pt-2 pb-2.5",
        marca.on ? CLASE_DE_TARJETA_RESALTADA : "border-border bg-card",
      )}
    >
      <div className="flex items-center gap-1.5">
        <Rotulo>Texto</Rotulo>
        <span className="text-ink-hint font-mono text-[0.625rem] font-semibold">
          {horaDeCaptura(capturadoEn)}
        </span>
        {marca.tag}
        <span className="ml-auto">
          <BotonDescartarDeTerreno uuid={uuid} etiqueta={etiqueta} />
        </span>
      </div>
      <p className="text-ink-body mt-0.5 text-xs leading-normal font-medium text-pretty">{texto}</p>
    </div>
  );
}

function BotonDeFotoDelPanel({
  informeId,
  foto,
  archivo,
  nombreDelCampo,
  onAbrir,
}: {
  informeId: number;
  foto: FotoDeTerreno;
  archivo: NonNullable<FotoDeTerreno["archivo"]>;
  nombreDelCampo: string;
  onAbrir: () => void;
}) {
  const marca = useResaltadoDeLaCaptura(foto.sincronizacion_id);

  return (
    <div className="relative">
    <button
      {...marca.atributo}
      type="button"
      onClick={onAbrir}
      aria-label={`Ampliar fotografía · ${nombreDelCampo}`}
      title="Ampliar"
      className={cn(
        "bg-muted hover:ring-primary focus-visible:ring-ring/50 relative aspect-[4/3] overflow-hidden rounded-[0.5625rem] outline-none hover:ring-2 focus-visible:ring-[3px]",
        marca.on && CLASE_DE_FOTO_RESALTADA,
      )}
    >
      <ImagenDeTerreno
        informeId={informeId}
        uuid={foto.uuid}
        hash={archivo.hash}
        mime={archivo.mime}
        alt={`Fotografía · ${nombreDelCampo}`}
      />
      <span className="bg-card/90 text-ink-body absolute bottom-1 left-1 rounded-sm px-1 py-px font-mono text-[0.5625rem] font-bold">
        {horaDeCaptura(foto.capturado_en)}
      </span>
    </button>
    <span className="bg-card/90 absolute top-1 right-1 rounded-sm">
      <BotonDescartarDeTerreno uuid={foto.uuid} etiqueta={`fotografía · ${nombreDelCampo}`} />
    </span>
    </div>
  );
}

/**
 * Lo que el encargado mandó para UNA descripción —audios con su transcripción,
 * textos y fotos—, al lado del campo que se redacta a partir de eso.
 *
 * Es un panel de LECTURA: no copia nada al campo ni lo propone. Tiene cuatro
 * caras y ninguna impide seguir escribiendo: cargando, error, "aún sin
 * sincronizar" (la App nunca mandó nada) y "no envió registros para esta
 * descripción" (mandó, pero no de esto).
 */
export function PanelDeTerrenoDeLaDescripcion({
  informeId,
  terreno,
  vista,
  campo,
  asignado,
  onVerTodo,
  onAbrirFoto,
  variante = "lateral",
  acciones,
}: PanelDeTerrenoDeLaDescripcionProps) {
  const desplegable = variante === "desplegable";
  const [abierto, setAbierto] = useState(false);
  // Quien tiene ASIGNADA la visita manda sobre quien sincronizó: el copy de
  // "aún sin sincronizar" habla de a quién se espera, y esa persona puede ser
  // distinta de la que mandó la última tanda.
  const nombre = asignado ?? vista?.encargado ?? null;
  const sinSincronizar = terreno.estado === "ok" && vista !== null && !vista.sincronizado;
  const resumen = resumenDelCampo(campo);
  const hayRegistros = resumen.audios + resumen.fotos + resumen.textos > 0;

  const meta =
    terreno.estado === "ok"
      ? sinSincronizar
        ? "Sin sincronizar"
        : textoDelResumen(resumen)
      : terreno.estado === "error"
        ? "No disponible"
        : "Cargando…";

  return (
    <div
      className={cn(
        "border-border bg-superficie-sutil flex min-w-0 flex-col overflow-hidden border-[1.5px]",
        desplegable ? "rounded-[0.875rem]" : "rounded-lg",
      )}
    >
      <div className="bg-card border-divisor flex flex-none items-center gap-2.5 border-b px-3 py-[0.5625rem]">
        <Avatar className="size-6">
          <AvatarFallback className="bg-success-soft-foreground text-primary-foreground text-[0.59375rem] font-extrabold">
            {nombre ? obtenerIniciales(nombre) : "?"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="text-ink text-xs font-bold">Registro de terreno</div>
          <div className="text-ink-muted truncate text-[0.65625rem] font-semibold">{meta}</div>
        </div>
        {desplegable ? (
          <>
            {acciones}
            <button
              type="button"
              aria-expanded={abierto}
              onClick={() => setAbierto((actual) => !actual)}
              className="text-ink-body hover:text-ink focus-visible:ring-ring/50 inline-flex flex-none items-center gap-1 rounded-sm text-[0.6875rem] font-bold outline-none focus-visible:ring-[3px]"
            >
              {abierto ? "Ocultar" : "Ver registros"}
              <ChevronDown
                className={cn("size-3 transition-transform", abierto && "rotate-180")}
                strokeWidth={2.4}
                aria-hidden="true"
              />
            </button>
          </>
        ) : onVerTodo ? (
          <button
            type="button"
            onClick={onVerTodo}
            title="Abrir pestaña Registro de terreno"
            className="text-success-soft-foreground hover:text-ink inline-flex flex-none items-center gap-1 text-[0.6875rem] font-bold"
          >
            Ver todo
            <ChevronRight className="size-3" strokeWidth={2.4} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {desplegable && !abierto ? null : (
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2.5",
          desplegable ? "max-h-[21.25rem]" : "max-h-80",
        )}
      >
        {terreno.estado === "cargando" ? (
          <>
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </>
        ) : terreno.estado === "error" ? (
          <p className="text-ink-muted px-2.5 py-5 text-center text-xs font-semibold text-pretty">
            No pudimos cargar el registro de terreno. Puedes seguir escribiendo y guardar: no
            depende de él.
          </p>
        ) : sinSincronizar ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-6 text-center">
            <span className="bg-card text-ink-muted flex size-9 items-center justify-center rounded-full border-[1.5px] border-dashed">
              <RotateCw className="size-[1.0625rem]" aria-hidden="true" />
            </span>
            <div className="text-ink text-[0.78125rem] font-bold">Aún sin sincronizar</div>
            <p className="text-ink-muted max-w-[17rem] text-[0.71875rem] leading-snug font-semibold text-pretty">
              {nombre
                ? `${nombre} tiene asignada la visita. Cuando sincronice desde la App, sus audios, fotos y textos aparecerán aquí.`
                : "La visita está asignada. Cuando el encargado sincronice desde la App, sus audios, fotos y textos aparecerán aquí."}
            </p>
          </div>
        ) : !hayRegistros ? (
          <p className="text-ink-muted px-2.5 py-5 text-center text-xs font-semibold text-pretty">
            El encargado no envió registros para esta descripción.
          </p>
        ) : (
          <>
            {campo?.audios.map((audio) => (
              <TarjetaDeAudio key={audio.uuid} informeId={informeId} audio={audio} />
            ))}

            {campo?.textos.map((texto) => (
              <TarjetaDeTexto
                key={texto.uuid}
                uuid={texto.uuid}
                etiqueta={`texto · ${campo?.nombre ?? "registro de terreno"}`}
                texto={texto.texto}
                capturadoEn={texto.capturado_en}
                sincronizacionId={texto.sincronizacion_id}
              />
            ))}

            {campo && campo.fotos.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5">
                {campo.fotos.map((foto) =>
                  foto.archivo ? (
                    <BotonDeFotoDelPanel
                      key={foto.uuid}
                      informeId={informeId}
                      foto={foto}
                      archivo={foto.archivo}
                      nombreDelCampo={campo.nombre}
                      onAbrir={() => onAbrirFoto(foto.uuid)}
                    />
                  ) : null,
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
      )}
    </div>
  );
}
